import { baza } from "@/lib/baza";
import {
  dohvatiGradilista,
  dohvatiRaspored,
  type Gradiliste,
  type Raspored,
} from "@/lib/gradilista";
import { dohvatiRadnike, type Radnik } from "@/lib/radnici";

/**
 * The one thing the entry screen is allowed to read offline — ADR-0004 §2.2.
 *
 * A site, its default shift, and who is rostered on it. Read only, and
 * nothing else: no salaries, no permits, no personnel register. The line in
 * §2.2 is the most important one in the document, because the moment offline
 * grows past hours entry the Faza 1 deadline goes with it.
 *
 * This is a copy of what the server already holds, so it may be thrown away
 * at any time — unlike the queue next to it, which exists nowhere else. When
 * the copy is missing the screen says the roster has not been loaded yet
 * rather than showing an empty site, because "nobody is rostered" and "this
 * phone has not been online since the roster was made" are different facts
 * and only one of them is true.
 */

export interface SnimljenKontekst {
  gradilista: Gradiliste[];
  /** The roster for `datum`, not for all time. */
  raspored: Raspored[];
  /** Names and the two blocking statuses. Team scope, so: his own crew. */
  radnici: Radnik[];
  /** The day the roster was taken for, ISO. */
  datum: string;
  /** When it was taken, ISO. Shown when the copy is old. */
  osvjezeno: string;
}

/**
 * Stored per day, keyed by the date it is the roster for.
 *
 * One slot would be wrong the moment somebody enters a day they missed: the
 * roster fetched for Monday would sit under the same key as today's and the
 * screen would show one day's crew under another day's heading. Rosters are
 * intervals, so the two genuinely can differ - somebody joined the site on
 * Tuesday.
 */
export async function spremiKontekst(
  kontekst: SnimljenKontekst,
): Promise<void> {
  await (await baza()).put("kontekst", kontekst, kontekst.datum);
}

export async function procitajKontekst(
  datum: string,
): Promise<SnimljenKontekst | null> {
  return (await (await baza()).get("kontekst", datum)) ?? null;
}

/**
 * Fetch the roster and keep a copy.
 *
 * Throws when the network is not there, and the caller falls back to the
 * stored copy. Not caught here on purpose: this function's job is to refresh,
 * and a refresh that silently succeeds by returning yesterday's data is how a
 * screen ends up lying about which day it is showing.
 */
export async function osvjeziKontekst(
  datum: string,
  signal?: AbortSignal,
): Promise<SnimljenKontekst> {
  const gradilista = await dohvatiGradilista(true, signal);
  const [raspored, radnici] = await Promise.all([
    dohvatiRaspored(undefined, datum, signal),
    dohvatiRadnike({ limit: 200, samo_aktivni: true }, signal),
  ]);

  const kontekst: SnimljenKontekst = {
    gradilista,
    raspored,
    radnici: radnici.items,
    datum,
    osvjezeno: new Date().toISOString(),
  };
  await spremiKontekst(kontekst);
  return kontekst;
}

export interface RadnikNaGradilistu {
  radnikId: string;
  ime: string;
  radnoMjesto: string | null;
  /** Set when this person may not be on site at all; the reason is shown. */
  zapreka: string | null;
}

/**
 * Who is on this site on this day, with the reason anybody may not work.
 *
 * The blocking rules are the two the site manager can see under §7 — an
 * expired permit and a medical certificate that says no. He is told that
 * somebody may not work and why in those words, never what the medical
 * finding was, which he has no right to and no use for.
 */
export function radniciNaGradilistu(
  kontekst: SnimljenKontekst,
  gradilisteId: string,
): RadnikNaGradilistu[] {
  const poId = new Map(
    kontekst.radnici.filter((r) => r.id).map((r) => [r.id as string, r]),
  );

  return kontekst.raspored
    .filter((stavka) => stavka.gradiliste_id === gradilisteId)
    .map((stavka) => {
      const radnik = poId.get(stavka.radnik_id);
      const ime =
        stavka.radnik ??
        (radnik ? `${radnik.prezime ?? ""} ${radnik.ime ?? ""}`.trim() : "");

      return {
        radnikId: stavka.radnik_id,
        ime: ime || "Nepoznat radnik",
        radnoMjesto: radnik?.radno_mjesto ?? null,
        zapreka: zapreka(radnik) || stavka.blocking_reasons?.join("; ") || null,
      };
    })
    .sort((a, b) => a.ime.localeCompare(b.ime, "hr"));
}

function zapreka(radnik: Radnik | undefined): string | null {
  if (!radnik) {
    return null;
  }
  if (radnik.zdravstveni_status?.smije_raditi === false) {
    return "Liječnički pregled nije važeći.";
  }
  if (radnik.dozvola_status?.kriticno) {
    return "Dozvola za rad je istekla.";
  }
  return null;
}
